/**
 * P3-5 发票工作流组件
 *
 * 嵌入客户详情页"业务"tab。
 * 顶部 [+ 申请开发票] → RequestInvoiceModal
 * 下方列出该客户所有发票，按 request_status 显示不同操作按钮
 */
import { useEffect, useRef, useState } from 'react';
import { Modal, Input, InputNumber, Radio, message } from 'antd';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { $, api } from '../lib/api';
import { useCustomerData } from '../lib/useCustomerData';
// 合页就地审核：财务在本卡直接开票/退回，按 invoice:approve 权限点显隐入口（审批主体仍是后端薄壳委托 approvalsSvc，端点不动）
import { useHasPermission } from '../lib/permissions';
import { Btn, Pill } from './ui';
// ux9 日期：统一用 lib/format 出口
import { fmtDateTime } from '../lib/format';

const STATUS_LABEL: Record<string, { label: string; tone: any }> = {
  pending_open: { label: '🟡 申请中', tone: 'warning' },
  opened: { label: '🟢 已开', tone: 'success' },
  sent_to_customer: { label: '🔵 已发', tone: 'primary' },
  confirmed: { label: '✅ 已确认', tone: 'success' },
  rejected: { label: '🔴 退回', tone: 'danger' },
};

export function InvoiceWorkflowCard({
  customerId, customerName, defaultAmount, embedded = false,
}: {
  customerId: number;
  customerName?: string;
  defaultAmount?: number;
  // 合页内嵌开关：true 时去掉最外层 .detail-section 外壳（只渲染内部内容），供发票段内嵌避免双层卡；默认 false 行为与现有列表/tab 完全一致
  embedded?: boolean;
}) {
  const qc = useQueryClient();
  const [showRequest, setShowRequest] = useState(false);
  // ux1 防重：标记已发/已确认共享同一回路，用 actingId 精确到行（非整列齐禁）
  const [actingId, setActingId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [viewingImage, setViewingImage] = useState<string | null>(null);
  const [openingInvoice, setOpeningInvoice] = useState<any>(null);
  const [imageUrl, setImageUrl] = useState('');
  // item3②：发票图改为员工直传到自己的发票（pending_open 时上传）。上传后存 inv.imageUrl，财务工作台只看图+通过/退回。
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const [previews, setPreviews] = useState<Record<number, string>>({});
  // 抽出核心上传：onChange(选文件) 与 onPaste(Ctrl+V 粘贴) 共用——仍走专用端点 /invoices/:id/upload-image（不换 ScreenshotUpload，端点不同）
  const uploadInvoiceImageFile = async (invoiceId: number, file: File) => {
    if (!file) return;
    setUploadingId(invoiceId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/invoices/${invoiceId}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = (r.data && (r.data as any).data) ? (r.data as any).data : r.data;
      setPreviews((p) => ({ ...p, [invoiceId]: (d as any).preview ?? (d as any).url }));
      message.success('发票图已上传，等待财务审核');
      qc.invalidateQueries({ queryKey: ['customer-invoices', customerId] });
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? err?.message ?? '上传失败');
    } finally {
      setUploadingId(null);
    }
  };
  const onUploadInvoiceImage = (invoiceId: number, e: any) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允许重复选同一文件
    if (file) uploadInvoiceImageFile(invoiceId, file);
  };
  // Ctrl+V 粘贴：用 React 合成 onPaste（限聚焦在该上传框时触发），从 clipboardData.items 找 image File。
  // 用局部 onPaste 而非 window 全局监听——天然按聚焦框区分，列表里多张 pending_open 发票不会一次粘贴串台。
  const onPasteInvoiceImage = (invoiceId: number, e: any) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type?.startsWith('image/')) {
        const blob = items[i].getAsFile();
        if (blob) { e.preventDefault(); uploadInvoiceImageFile(invoiceId, blob); break; }
      }
    }
  };

  const { data: invoices } = useQuery<any>({
    queryKey: ['customer-invoices', customerId],
    queryFn: () => $.get(`/invoices`, { customerId, pageSize: 50 }),
  });
  const items: any[] = invoices?.items ?? [];

  // ux1：onAction 跟踪 actingId，按钮按行 disabled，finally 复位
  const onAction = (label: string, id: number, p: Promise<any>) => {
    setActingId(id);
    return p.then(() => {
      message.success(label);
      qc.invalidateQueries({ queryKey: ['customer-invoices', customerId] });
    }).catch((e: any) => message.error(`${label} 失败：${e?.message ?? ''}`))
      .finally(() => setActingId(null));
  };

  const markSent = (id: number) => onAction('已标记"已发"', id, $.post(`/invoices/${id}/sent`, {}));
  const markConfirmed = (id: number) => onAction('已标记"已确认"', id, $.post(`/invoices/${id}/confirmed`, {}));

  // 合页就地审核门控：有 invoice:approve 权限处都给就地审（财务程华庆/academic_director/manager/admin/director 持有）；无则不渲染开票/退回入口
  const canApproveInvoice = useHasPermission('invoice:approve');

  // 财务开票（只通过、不带图，图由员工已上传）——复刻 FinanceWorkbench openInv；缺图由按钮 disabled 兜底，这里不再传图
  const openInv = useMutation({
    mutationFn: (id: number) => $.post(`/invoices/${id}/open`, {}),
    onSuccess: () => {
      message.success('已开发票，已通知销售员');
      qc.invalidateQueries({ queryKey: ['customer-invoices', customerId] });
      qc.invalidateQueries({ queryKey: ['customer', String(customerId)] });
    },
    onError: (e: any) => message.error(e?.message ?? '开票失败'),
  });

  // 财务退回（必填理由）——复刻 FinanceWorkbench reject；复用本组件已有 rejectingId/rejectReason 状态承接退回弹窗
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => $.post(`/invoices/${id}/reject`, { reason }),
    onSuccess: () => {
      message.success('已退回');
      setRejectingId(null); setRejectReason('');
      qc.invalidateQueries({ queryKey: ['customer-invoices', customerId] });
      qc.invalidateQueries({ queryKey: ['customer', String(customerId)] });
    },
    onError: (e: any) => message.error(e?.message ?? '退回失败'),
  });

  // 内部内容（外壳无关）；embedded=true 用 Fragment 去壳，默认用 .detail-section 外壳，列表/tab 现用法零变
  const inner = (
    <>
      {/* 合页内嵌(embedded=true)时不渲染内部「📋 发票」detail-section-title：发票段外层 SectionCard 已有「发票」标题，避免双标题。
          但「+ 申请开发票」按钮原嵌在该标题行内，去标题不能丢按钮——内嵌时单独右对齐渲染该按钮（无标题文字）；
          非内嵌(列表/旧用法)保持原标题行整体不变、零回归。 */}
      {embedded ? (
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ marginLeft: 'auto' }}>
            <Btn kind="primary" onClick={() => setShowRequest(true)}>+ 申请开发票</Btn>
          </div>
        </div>
      ) : (
        <div className="detail-section-title" style={{ display: 'flex', alignItems: 'center' }}>
          <span>📋 发票</span>
          <div style={{ marginLeft: 'auto' }}>
            <Btn kind="primary" onClick={() => setShowRequest(true)}>+ 申请开发票</Btn>
          </div>
        </div>
      )}

      {items.length === 0 && (
        <div style={{ padding: '12px 0', fontSize: 13, color: 'var(--text-3)' }}>
          暂无发票申请。需要开发票时点右上"+ 申请开发票"。
        </div>
      )}

      {items.map((inv: any) => {
        const meta = STATUS_LABEL[inv.requestStatus] ?? { label: inv.status ?? '—', tone: 'default' };
        return (
          <div key={inv.id} style={{
            padding: 12, border: '1px solid var(--border)', borderRadius: 6, marginTop: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Pill tone={meta.tone}>{meta.label}</Pill>
              <span style={{ fontWeight: 500 }}>¥{Number(inv.amount).toLocaleString()}</span>
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{inv.headerName}</span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)' }}>
                {inv.invoiceCode}
              </span>
            </div>

            {inv.rejectReason && (
              <div style={{ padding: 8, background: 'var(--danger-bg)', borderRadius: 4, fontSize: 12, marginBottom: 6 }}>
                退回理由：{inv.rejectReason}
              </div>
            )}

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {inv.requestStatus === 'opened' && inv.imageUrl && (
                <>
                  <Btn kind="default" onClick={() => setViewingImage(inv.imageUrl)}>查看图片</Btn>
                  <a href={inv.imageUrl} download style={{ textDecoration: 'none' }}>
                    <Btn kind="default">下载</Btn>
                  </a>
                  <Btn kind="primary" onClick={() => markSent(inv.id)} disabled={actingId === inv.id}>
                    {actingId === inv.id ? '提交中…' : '✓ 已发送给客户'}
                  </Btn>
                </>
              )}
              {inv.requestStatus === 'sent_to_customer' && (
                <>
                  <Btn kind="primary" onClick={() => markConfirmed(inv.id)} disabled={actingId === inv.id}>
                    {actingId === inv.id ? '提交中…' : '✓ 客户已确认'}
                  </Btn>
                </>
              )}
              {inv.requestStatus === 'confirmed' && (
                <span style={{ color: 'var(--success)', fontSize: 12 }}>闭环完成 🎉</span>
              )}
              {inv.requestStatus === 'rejected' && (
                <Btn kind="primary" onClick={() => setShowRequest(true)}>重新申请</Btn>
              )}
            </div>

            {/* item3②：申请中(待开)——员工把开好的发票图直传，财务工作台只看图+通过/退回 */}
            {inv.requestStatus === 'pending_open' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
                {(previews[inv.id] || inv.imageUrl) ? (
                  <>
                    <span style={{ fontSize: 12, color: 'var(--success)' }}>✓ 已上传发票图，等待财务审核</span>
                    <Btn kind="default" onClick={() => setViewingImage(previews[inv.id] || inv.imageUrl)}>查看图片</Btn>
                    <label tabIndex={0} onPaste={(e) => onPasteInvoiceImage(inv.id, e)}
                           style={{ fontSize: 12, color: 'var(--primary)', cursor: uploadingId === inv.id ? 'wait' : 'pointer' }}>
                      {uploadingId === inv.id ? '上传中…' : '点此粘贴(Ctrl+V) / 选文件'}
                      <input type="file" accept="image/*" style={{ display: 'none' }}
                             disabled={uploadingId === inv.id}
                             onChange={(e) => onUploadInvoiceImage(inv.id, e)} />
                    </label>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>开好发票后，上传发票 / 收据图（jpg / png，≤10MB），财务审核通过即生效：</span>
                    <label tabIndex={0} onPaste={(e) => onPasteInvoiceImage(inv.id, e)}
                           style={{
                             fontSize: 13, padding: '4px 12px', borderRadius: 4, cursor: uploadingId === inv.id ? 'wait' : 'pointer',
                             border: '1px solid var(--primary)', color: 'var(--primary)',
                           }}>
                      {uploadingId === inv.id ? '上传中…' : '📎 点此粘贴(Ctrl+V) / 选文件'}
                      <input type="file" accept="image/*" style={{ display: 'none' }}
                             disabled={uploadingId === inv.id}
                             onChange={(e) => onUploadInvoiceImage(inv.id, e)} />
                    </label>
                  </>
                )}
              </div>
            )}

            {/* 合页就地审核：仅对待开票(pending_open)行、且持 invoice:approve 权限者，补「开票」「退回」就地按钮。
                审批主体仍是后端硬校验，前端只按权限点显隐入口、不自判审批人。 */}
            {inv.requestStatus === 'pending_open' && canApproveInvoice && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>财务审核：</span>
                {/* 缺图禁用开票（图由员工已上传）：inv.imageUrl falsy 时 disabled + 提示（对齐 FinanceWorkbench 缺图处理） */}
                <Btn kind="primary"
                     onClick={() => openInv.mutate(inv.id)}
                     disabled={!inv.imageUrl || openInv.isPending}>
                  {openInv.isPending ? '提交中…' : '开票'}
                </Btn>
                {!inv.imageUrl && (
                  <span style={{ fontSize: 12, color: 'var(--warning, #b7791f)' }}>待员工上传发票图</span>
                )}
                <Btn kind="default" onClick={() => { setRejectingId(inv.id); setRejectReason(''); }}>退回</Btn>
              </div>
            )}

            <Timeline timeline={inv.timelineJson ?? []} />
          </div>
        );
      })}

      <RequestInvoiceModal
        open={showRequest}
        onClose={() => setShowRequest(false)}
        customerId={customerId}
        customerName={customerName}
        defaultAmount={defaultAmount}
        onDone={() => {
          setShowRequest(false);
          qc.invalidateQueries({ queryKey: ['customer-invoices', customerId] });
        }}
      />

      <Modal
        title="发票图片"
        open={!!viewingImage}
        onCancel={() => setViewingImage(null)}
        footer={null}
        width={680}
      >
        {viewingImage && <img src={viewingImage} alt="invoice" style={{ width: '100%' }} />}
      </Modal>

      {/* 合页就地退回：必填理由（空理由禁止），复刻 FinanceWorkbench 退回弹窗 */}
      <Modal
        title="退回发票申请"
        open={!!rejectingId}
        onCancel={() => { if (!reject.isPending) { setRejectingId(null); setRejectReason(''); } }}
        footer={
          <>
            <Btn kind="default" onClick={() => { setRejectingId(null); setRejectReason(''); }} disabled={reject.isPending}>取消</Btn>
            <Btn kind="primary"
                 onClick={() => rejectingId && reject.mutate({ id: rejectingId, reason: rejectReason })}
                 disabled={!rejectReason || reject.isPending}>
              {reject.isPending ? '提交中…' : '确认退回'}
            </Btn>
          </>
        }
      >
        <Input.TextArea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                        rows={4} placeholder="如：抬头错误 / 税号缺失 / 金额对不上 ..." />
      </Modal>
    </>
  );

  // embedded=true：去外壳直接返回内部内容（供合页发票段内嵌）；默认/false：保持 .detail-section 外壳（现用法零变）
  return embedded ? inner : <div className="detail-section">{inner}</div>;
}

function Timeline({ timeline }: { timeline: any[] }) {
  if (!timeline || timeline.length === 0) return null;
  const labelMap: Record<string, string> = {
    requested: '📋 申请', opened: '✅ 开具', sent_to_customer: '📤 发送',
    confirmed: '🎉 确认', rejected: '❌ 退回',
  };
  return (
    <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
      {timeline.map((t: any, i: number) => (
        <div key={i} style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 2 }}>
          <span style={{ marginRight: 8 }}>{fmtDateTime(t.at)}</span>
          {labelMap[t.event] ?? t.event}
          {t.actor_name && <span> · {t.actor_name}</span>}
          {t.extra?.reason && <span> · {t.extra.reason}</span>}
        </div>
      ))}
    </div>
  );
}

function RequestInvoiceModal({
  open, onClose, customerId, customerName, defaultAmount, onDone,
}: {
  open: boolean;
  onClose: () => void;
  customerId: number;
  customerName?: string;
  defaultAmount?: number;
  onDone: () => void;
}) {
  // v1.2 Batch 2-B：useCustomerData 跨步骤 prefill — 抬头/金额/邮箱来自 customer 表
  const { prefill, customer } = useCustomerData(customerId);
  // ux10：弹窗打开后自动聚焦「抬头」输入框（客户字段 disabled，抬头是第一个可填项）
  const titleRef = useRef<any>(null);
  const [form, setForm] = useState({
    header_type: 'individual' as 'individual' | 'company',
    // item4：抬头【不】用 customerName(=昵称)兜底初值——否则下方 useEffect 的 `f.invoice_title || prefill.realName`
    //   会因初值已是昵称而短路，realName 永远填不进去。留空让 useEffect 用「realName 优先」回填。
    invoice_title: '',
    tax_number: '',
    amount: defaultAmount ?? 0,
    type: 'invoice',
    recipient_email: '',
    notes: '',
  });

  // 客户数据加载后回填空字段（已填的不覆盖，用户手改不被冲掉）
  useEffect(() => {
    if (!prefill) return;
    setForm((f) => ({
      ...f,
      invoice_title: f.invoice_title || prefill.realName || prefill.nickname,
      amount: f.amount || prefill.totalDueCny,
      recipient_email: f.recipient_email || prefill.email,
    }));
  }, [prefill]);

  const submit = useMutation({
    mutationFn: () => $.post(`/invoices/request`, {
      customer_id: customerId,
      ...form,
    }),
    onSuccess: () => { message.success('已提交开票申请'); onDone(); },
    onError: (e: any) => message.error(e?.message ?? '提交失败'),
  });

  return (
    <Modal
      title="申请开发票"
      open={open}
      onCancel={onClose}
      afterOpenChange={(o) => { if (o) titleRef.current?.focus?.(); }}
      footer={
        <>
          <Btn kind="default" onClick={onClose}>取消</Btn>
          <Btn kind="primary"
               onClick={() => submit.mutate()}
               disabled={!form.invoice_title || !form.amount || submit.isPending}>
            {submit.isPending ? '提交中…' : '提交申请'}
          </Btn>
        </>
      }
      width={520}
    >
      <Field label="客户">
        <Input value={customerName ?? `#${customerId}`} disabled />
      </Field>
      <Field label="抬头类型">
        <Radio.Group value={form.header_type} onChange={(e) => setForm({ ...form, header_type: e.target.value })}>
          <Radio value="individual">个人</Radio>
          <Radio value="company">公司</Radio>
        </Radio.Group>
      </Field>
      <Field label="抬头">
        <Input ref={titleRef} value={form.invoice_title}
               onChange={(e) => setForm({ ...form, invoice_title: e.target.value })}
               placeholder={form.header_type === 'individual' ? '个人姓名' : '公司全称'} />
      </Field>
      {/* item4：抬头默认带出客户「真实姓名」(已在 useEffect 回填)；真名为空则提示先去客户页补，避免拿昵称兜底当抬头。
          判据用【原始】customer.realName——prefill.realName 已用昵称兜底(useCustomerData:83)永不为空、不能用来判“真名是否填了”。 */}
      {customer && !(customer.realName ?? '').trim() && (
        <div style={{ marginTop: -6, marginBottom: 12, fontSize: 12, color: '#b7791f' }}>
          ⚠️ 该客户「真实姓名」未填，抬头暂用昵称兜底。建议先到客户页补「真实姓名」，再开票更准确。
        </div>
      )}
      {form.header_type === 'company' && (
        <Field label="税号">
          <Input value={form.tax_number}
                 onChange={(e) => setForm({ ...form, tax_number: e.target.value })}
                 placeholder="统一社会信用代码" />
        </Field>
      )}
      <Field label="金额">
        <InputNumber value={form.amount}
                     onChange={(v) => setForm({ ...form, amount: Number(v ?? 0) })}
                     style={{ width: '100%' }} />
      </Field>
      <Field label="类型">
        <Radio.Group value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
          <Radio value="receipt">收据</Radio>
          <Radio value="invoice">普票</Radio>
          <Radio value="special">专票</Radio>
        </Radio.Group>
      </Field>
      <Field label="收件邮箱（可选）">
        <Input value={form.recipient_email}
               onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} />
      </Field>
      <Field label="备注（可选）">
        <Input.TextArea value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
      </Field>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, marginBottom: 4, color: 'var(--text-2)' }}>{label}</div>
      {children}
    </div>
  );
}
